import React, { useState } from 'react';
import { User, Users, UserRound, Mail, Phone, Calendar, Building2, Briefcase, DollarSign, MapPin, Clock, Loader2, X, Copy } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { formatDate, formatPhone, formatSalary, decodeAddress } from '../utils/formatters';

function activeTeamMemberships(employee) {
  const memberships = Array.isArray(employee?.team_memberships) ? employee.team_memberships : [];
  return memberships.filter((membership) => {
    if (!membership || membership.active === false) return false;
    const team = membership.team;
    return Boolean(team?.name) && team.active !== false;
  });
}

function supervisorInfo(employee, team) {
  const leaderId = Number(team?.leader?.id || team?.leader_id || 0);
  if (!leaderId) return { kind: 'none', label: 'Sin supervisor' };
  if (Number(employee?.id) === leaderId) return { kind: 'self', label: 'Es el supervisor' };
  const leader = team?.leader;
  const name = [leader?.first_name, leader?.last_name].filter(Boolean).join(' ').trim();
  if (!name) return { kind: 'none', label: 'Sin supervisor' };
  return { kind: 'named', label: name };
}

const SUPERVISOR_BADGE = {
  none: 'bg-gray-700 text-gray-300',
  self: 'bg-emerald-900/50 text-emerald-200',
  named: 'bg-blue-900/50 text-blue-200',
};

export const EmployeeDetails = ({ employee, loading, onClose, extra = null }) => {
  const [emailCopied, setEmailCopied] = useState(false);

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="flex flex-col items-center text-gray-400">
          <Loader2 className="animate-spin mb-4" size={32} />
          <p>Cargando detalles del empleado...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Selecciona un empleado para ver sus detalles</p>
        </div>
      </div>
    );
  }

  const fullName = employee.first_name && employee.last_name
    ? `${employee.first_name} ${employee.last_name}`
    : employee.first_name || employee.last_name || 'N/A';
  const teams = activeTeamMemberships(employee);

  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg flex flex-col h-full min-h-0 max-h-[700px]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <User className="w-5 h-5 text-gray-400" />
          <h2 className="text-xl font-semibold text-white">
            Detalles del Empleado
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
              title="Cerrar detalles"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Información Básica */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-400" />
              Información Básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Nombre Completo</p>
                <p className="text-white font-medium">{fullName}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4 min-w-0 overflow-hidden">
                <p className="text-xs text-gray-400 mb-1">Email</p>
                <div className="flex items-center gap-2 min-w-0">
                  <a
                    href={`mailto:${employee.email}`}
                    className="text-blue-400 hover:text-blue-300 hover:underline truncate min-w-0"
                    title={employee.email || ''}
                  >
                    {employee.email || 'N/A'}
                  </a>
                  {employee.email && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        navigator.clipboard.writeText(employee.email);
                        setEmailCopied(true);
                        setTimeout(() => setEmailCopied(false), 2000);
                      }}
                      className="shrink-0 p-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-blue-400 transition-colors"
                      title={emailCopied ? 'Copiado' : 'Copiar email'}
                    >
                      {emailCopied ? (
                        <span className="text-xs text-emerald-400">Copiado</span>
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Teléfono</p>
                <p className="text-white">{formatPhone(employee.phone)}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Workday ID</p>
                <p className="text-white">{employee.workday_id || 'N/A'}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Estado</p>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    className={
                      employee.active
                        ? 'bg-emerald-900/50 text-emerald-200'
                        : 'bg-red-900/50 text-red-200'
                    }
                  >
                    {employee.active ? 'Activo' : 'Inactivo'}
                  </Badge>
                  {employee.external && (
                    <Badge className="bg-blue-900/50 text-blue-200">
                      Externo
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Información Laboral */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-gray-400" />
              Información Laboral
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Base Fábrica</p>
                <Badge className="bg-violet-900/50 text-violet-200">
                  {employee.factory_base || 'N/A'}
                </Badge>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Tipo de Contrato</p>
                <p className="text-white">{employee.contract_type || 'N/A'}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Fecha de Contratación</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-white">{formatDate(employee.hire_date)}</p>
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Salario</p>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <p className="text-white font-medium">{formatSalary(employee.salary)}</p>
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Título</p>
                <p className="text-white">{employee.BusinessTitle || 'N/A'}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Perfil de Trabajo</p>
                <p className="text-white">{employee.JobProfileID || 'N/A'}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Centro de Costos</p>
                <p className="text-white">{employee.CostCenter || 'N/A'}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4 md:col-span-2">
                <p className="text-xs text-gray-400 mb-3">Equipo y supervisor</p>
                {teams.length === 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-600 bg-gray-800/50 px-3 py-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-700 text-gray-400">
                        <Users className="w-4 h-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-200">Sin equipo</p>
                        <p className="text-xs text-gray-500">No está asignado a ninguno</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-600 bg-gray-800/50 px-3 py-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-700 text-gray-400">
                        <UserRound className="w-4 h-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-200">Sin supervisor</p>
                        <p className="text-xs text-gray-500">No hay líder asociado</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {teams.map((membership) => {
                      const supervisor = supervisorInfo(employee, membership.team);
                      return (
                        <li
                          key={membership.id || membership.team.id}
                          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-600 bg-gray-800/50 px-3 py-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-900/40 text-violet-200">
                              <Users className="w-4 h-4" />
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-white truncate">{membership.team.name}</p>
                              <p className="text-xs text-gray-500">Equipo</p>
                            </div>
                          </div>
                          <Badge className={SUPERVISOR_BADGE[supervisor.kind]}>
                            <UserRound className="w-3 h-3" />
                            {supervisor.label}
                          </Badge>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </section>

          {/* Dirección */}
          {employee.PathAddressData && (
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gray-400" />
                Dirección
              </h3>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-white whitespace-pre-line">
                  {decodeAddress(employee.PathAddressData)}
                </p>
              </div>
            </section>
          )}

          {/* Asignaciones de Fábrica/Departamento */}
          {employee.factory_assignments && employee.factory_assignments.length > 0 && (
            <section>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-400" />
                Asignaciones de Fábrica/Departamento
              </h3>
              <div className="space-y-2">
                {employee.factory_assignments.map((assignment, index) => (
                  <div key={index} className="bg-gray-700/50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Fábrica</p>
                        <p className="text-white">{assignment.factory_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Departamento</p>
                        <p className="text-white">{assignment.department_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Rol</p>
                        <p className="text-white">{assignment.role_name || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {extra}

          {/* Información del Sistema */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Información del Sistema
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employee.created_at && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Fecha de Creación</p>
                  <p className="text-white">{formatDate(employee.created_at)}</p>
                </div>
              )}
              {employee.updated_at && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Última Actualización</p>
                  <p className="text-white">{formatDate(employee.updated_at)}</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
